import type { OnDestroy, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppStateService } from './shared/services/app-state.service';
import { filter, Subject, switchMap, takeUntil } from 'rxjs';
import { StudiesService } from './shared/services/studies.service';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { addStyleToHead } from './shared/utils';
import { DomSanitizer } from '@angular/platform-browser';
import { TokenStorageService } from './shared/services/token-storage.service';
import { UserDetailsService } from './shared/services/user-details.service';
import { CommonDialogService } from './shared/components/popups/common.dialog.service';
import { ImportantAlertService } from './shared/services/important-alert.service';
import { ZendeskService } from './shared/services/zendesk.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private userDetailsPopupShown = false;
  private cguPopupShown = false;
  private importantAlertPopupShown = false;

  public showNav = false;
  public isScc = false;

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly studiesService: StudiesService,
    private readonly appStateService: AppStateService,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly tokenStorageService: TokenStorageService,
    private readonly userDetailsService: UserDetailsService,
    private readonly commonDialogService: CommonDialogService,
    private readonly importantAlertService: ImportantAlertService,
    private readonly zendeskService: ZendeskService
  ) {
    this.isScc = this.appStateService.isScc;

    this.matIconRegistry.addSvgIcon('vhs', this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/viewer/vhs.svg'));
    this.matIconRegistry.addSvgIcon('norberg', this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/viewer/norberg.svg'));
    this.matIconRegistry.addSvgIcon('angle_2_lines', this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/viewer/angle_2_lines.svg'));

    switch (this.appStateService.mode) {
      case 'scc':
        document.title = 'Plateforme LOF Vet - Société Centrale Canine';

        addStyleToHead('stylesheet', '/scc.css');
        addStyleToHead('stylesheet', 'https://fonts.googleapis.com/css2?family=PT+Sans:ital,wght@0,400;0,700;1,400;1,700&display=swap');
        addStyleToHead('stylesheet', 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;600&display=swap');
        addStyleToHead('icon', '/assets/img/scc/favicon.ico');
        break;
      case 'veokee':
        document.title = 'Veokee - Powered by MyPacs';

        addStyleToHead('stylesheet', '/veokee.css');
        addStyleToHead('icon', '/assets/img/veokee/favicon.ico');
        break;
      default:
        addStyleToHead('stylesheet', '/mypacs.css');
        addStyleToHead('icon', '/favicon.ico');
        break;
    }

    this.translate.addLangs(['en', 'fr', 'de', 'lb']);
    this.translate.setDefaultLang('fr');

    // Check for saved language preference first, then browser language, then default to French
    const savedLang = localStorage.getItem('user-language');
    const browserLang = this.translate.getBrowserLang();
    const languageToUse = savedLang?.match(/en|fr|de|lb/) ? savedLang :
      browserLang?.match(/en|fr|de|lb/) ? browserLang : 'fr';

    this.translate.use(languageToUse);
    this.dateAdapter.setLocale(languageToUse);

    if (!this.isScc) {
      this.zendeskService.init(languageToUse);
    }
  }

  ngOnInit(): void {
    this.studiesService.studyChanged.subscribe(study => {
      this.appStateService.notifyStudyChanged(study);
    });

    this.router.events
      .pipe(
        filter((e): e is ActivationEnd => e instanceof ActivationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const hiddenNav = this.isScc ? ['viewer'] : ['login', 'viewer', 'public', 'not-found', 'forgot-password'];
        this.showNav = hiddenNav.filter(path => event.snapshot.routeConfig?.path?.startsWith(path)).length === 0;

        // Show/hide Zendesk launcher based on authentication and page
        if (this.showNav && this.tokenStorageService.getToken()) {
          this.zendeskService.show();
        } else {
          this.zendeskService.hide();
        }

        this.checkAndShowUserDetailsPopup();

        if (!this.isScc) {
          this.checkAndShowImportantAlert();
        }
      });

    // Vérification unique lors du chargement initial pour les utilisateurs sans nom/prénom
    this.checkAndShowUserDetailsPopup();
  }

  private checkAndShowUserDetailsPopup(): void {
    // Vérification unique pour les utilisateurs sans nom/prénom lors de leur première connexion
    if (this.tokenStorageService.getRequireUserDetails() && !this.userDetailsPopupShown) {
      this.showUserDetailsPopup();
    } else {
      this.checkAndShowCguPopup();
    }
  }

  private showUserDetailsPopup(): void {
    this.userDetailsPopupShown = true;

    this.commonDialogService.openUserDetailsDialog()
      .pipe(
        switchMap(result => this.userDetailsService.updateUserDetails(result.firstName, result.lastName)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.tokenStorageService.markUserDetailsCompleted();
            this.checkAndShowCguPopup();
          } else {
            // Si l'update a échoué, permettre de réessayer
            this.userDetailsPopupShown = false;
          }
        },
        error: (error) => {
          console.error('Error updating user details:', error);
          // Si erreur, permettre de réessayer
          this.userDetailsPopupShown = false;
        }
      });
  }

  private checkAndShowCguPopup(): void {
    if (this.isScc) return;
    if (this.tokenStorageService.getRequireCgu() && !this.cguPopupShown) {
      this.showCguPopup();
    }
  }

  private showCguPopup(): void {
    this.cguPopupShown = true;

    this.commonDialogService.openCguDialog()
      .pipe(
        switchMap(() => this.userDetailsService.acceptCgu()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.tokenStorageService.markCguAccepted();
          } else {
            this.cguPopupShown = false;
          }
        },
        error: (error) => {
          console.error('Error accepting CGU:', error);
          this.cguPopupShown = false;
        }
      });
  }

  private checkAndShowImportantAlert(): void {
    if (!this.tokenStorageService.getToken()) {
      return;
    }

    if (this.importantAlertPopupShown) {
      return;
    }

    // Don't show important alerts on edit-report page to avoid stealing focus from Jodit editor
    const currentUrl = this.router.url;
    if (currentUrl.includes('/edit-report')) {
      return;
    }

    this.importantAlertPopupShown = true;

    this.importantAlertService.getUnviewedAlerts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (alerts) => {
          if (alerts && alerts.length > 0) {
            // Affiche les alertes séquentiellement
            this.showAlertsSequentially(alerts, 0);
          } else {
            this.importantAlertPopupShown = false;
          }
        },
        error: (error) => {
          console.error('Error fetching alerts:', error);
          this.importantAlertPopupShown = false;
        }
      });
  }

  private showAlertsSequentially(alerts: any[], index: number): void {
    if (index >= alerts.length) {
      return;
    }

    const alert = alerts[index];

    this.commonDialogService.openImportantAlertDialog(alert)
      .pipe(
        switchMap(() => this.importantAlertService.markAsViewed(alert.id)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          // Affiche l'alerte suivante après marquage
          this.showAlertsSequentially(alerts, index + 1);
        },
        error: (error) => {
          console.error('Error marking alert as viewed:', error);
          // Continue avec l'alerte suivante même en cas d'erreur
          this.showAlertsSequentially(alerts, index + 1);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
